import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarPicker } from './ui/calendar';
import { startOfWeek, endOfWeek, addWeeks, format, isSameDay, isWithinInterval, getDay } from 'date-fns';
import { countsTowardWeek } from '../lib/orderTotals';

const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekDates(refDate) {
  const start = startOfWeek(refDate, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

// Parse date safely — date-only strings are UTC midnight in JS, shifting to previous day in western TZs
const safeDate = (d) => {
  if (!d) return new Date();
  const s = typeof d === 'string' ? d : String(d);
  return new Date(s.length === 10 ? s + 'T12:00:00' : s);
};

// An order with no delivery date, or an unreadable one, is not on the strip at
// all — without this it would land on today, inflating today's box and the week.
const deliveryDate = (order) => {
  if (!order?.delivery_date) return null;
  const parsed = safeDate(order.delivery_date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const WeekView = ({
  orders = [],
  allowedDeliveryDays = null,
  onFilterChange,
  showRevenueSummary = false,
  className = '',
}) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const today = useMemo(() => new Date(), []);
  const currentWeekStart = useMemo(() => {
    const base = addWeeks(today, weekOffset);
    return startOfWeek(base, { weekStartsOn: 1 });
  }, [today, weekOffset]);

  const weekDates = useMemo(() => getWeekDates(currentWeekStart), [currentWeekStart]);
  const weekEnd = useMemo(() => endOfWeek(currentWeekStart, { weekStartsOn: 1 }), [currentWeekStart]);

  const weekLabel = `${format(weekDates[0], 'MMM d')} - ${format(weekDates[6], 'MMM d, yyyy')}`;

  // Orders grouped by day. The strip counts live work only — a cancelled or
  // failed delivery is not something the kitchen makes or the week earns — but
  // the full grouping is kept so the status cards can still census the week.
  const groupByDay = (list) => {
    const map = {};
    weekDates.forEach((date, i) => { map[i] = []; });
    list.forEach(order => {
      const delDate = deliveryDate(order);
      if (!delDate) return;
      weekDates.forEach((date, i) => {
        if (isSameDay(delDate, date)) map[i].push(order);
      });
    });
    return map;
  };

  const liveOrders = useMemo(() => orders.filter(countsTowardWeek), [orders]);

  const ordersByDay = useMemo(
    () => groupByDay(liveOrders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveOrders, weekDates],
  );

  const allOrdersByDay = useMemo(
    () => groupByDay(orders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, weekDates],
  );

  const inThisWeek = (list) => list.filter(order => {
    const d = deliveryDate(order);
    return d ? isWithinInterval(d, { start: weekDates[0], end: weekEnd }) : false;
  });

  // Week filtered orders
  const weekOrders = useMemo(
    () => inThisWeek(liveOrders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveOrders, weekDates, weekEnd],
  );

  const weekOrdersAllStatuses = useMemo(
    () => inThisWeek(orders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, weekDates, weekEnd],
  );

  // Revenue & items summary
  const weekSummary = useMemo(() => {
    const rev = weekOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
    const items = weekOrders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
    const qty = weekOrders.reduce((sum, o) =>
      sum + (o.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0), 0);
    return { revenue: rev, itemLines: items, totalQty: qty, orderCount: weekOrders.length };
  }, [weekOrders]);

  // Compute filtered result and notify parent. The second list is the same
  // selection with every status in it, so a parent can still count the week's
  // cancellations and bring them back when one is the active filter.
  const notifyParent = (dayIndex = selectedDay) => {
    if (dayIndex !== null) {
      onFilterChange?.(ordersByDay[dayIndex] || [], allOrdersByDay[dayIndex] || []);
    } else {
      onFilterChange?.(weekOrders, weekOrdersAllStatuses);
    }
  };

  const handleDayClick = (dayIndex) => {
    if (viewAll) return;
    const newDay = selectedDay === dayIndex ? null : dayIndex;
    setSelectedDay(newDay);
    notifyParent(newDay);
  };

  const handleWeekChange = (newOffset) => {
    setWeekOffset(newOffset);
    setSelectedDay(null);
    // Recalculate with new week - parent will get updated on next render via useEffect
  };

  const handleToggleViewAll = () => {
    const next = !viewAll;
    setViewAll(next);
    setSelectedDay(null);
    if (next) {
      onFilterChange?.(null, null);
    } else {
      onFilterChange?.(weekOrders, weekOrdersAllStatuses);
    }
  };

  const handleCalendarSelect = (date) => {
    if (!date) return;
    const diff = Math.round((startOfWeek(date, { weekStartsOn: 1 }) - startOfWeek(today, { weekStartsOn: 1 })) / (7 * 86400000));
    setWeekOffset(diff);
    setSelectedDay(null);
    setCalendarOpen(false);
  };

  // Notify parent when week changes
  React.useEffect(() => {
    if (!viewAll) {
      notifyParent();
    }
    // Intentionally re-notify only when the week changes — day/view-all clicks
    // call onFilterChange imperatively in their own handlers above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  return (
    <div className={`space-y-3 ${className}`} data-testid="week-view">
      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-700">Weekly Delivery Schedule</h3>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-amber-600 hover:text-amber-700 h-7 px-2"
            onClick={() => { setWeekOffset(0); setSelectedDay(null); }}
            data-testid="week-today-btn"
          >
            Today
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => handleWeekChange(weekOffset - 1)} data-testid="week-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 px-3 text-xs font-medium" data-testid="week-label">
                <Calendar className="h-3 w-3 mr-1" />
                {weekLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <CalendarPicker
                mode="single"
                selected={currentWeekStart}
                onSelect={handleCalendarSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => handleWeekChange(weekOffset + 1)} data-testid="week-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Day Boxes */}
      <div className="grid grid-cols-7 gap-2" data-testid="week-day-boxes">
        {weekDates.map((date, i) => {
          const dayName = DAY_NAMES[i];
          const count = (ordersByDay[i] || []).length;
          const isToday = isSameDay(date, today);
          const isSelected = selectedDay === i && !viewAll;
          const isDisabled = allowedDeliveryDays && !allowedDeliveryDays.includes(dayName);
          const dayRev = (ordersByDay[i] || []).reduce((s, o) => s + (o.total_amount || 0), 0);

          return (
            <div
              key={date.toISOString()}
              onClick={() => !isDisabled && handleDayClick(i)}
              className={`
                relative rounded-lg border-2 p-2 text-center transition-all cursor-pointer select-none
                ${isSelected ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-200' : ''}
                ${isToday && !isSelected ? 'border-amber-300 bg-amber-50/50' : ''}
                ${!isSelected && !isToday ? 'border-gray-200 hover:border-gray-300' : ''}
                ${isDisabled ? 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed' : ''}
              `}
              data-testid={`week-day-${dayName}`}
            >
              <p className={`text-xs font-semibold ${isToday ? 'text-amber-600' : 'text-gray-500'}`}>
                {DAY_LABELS[i]}
              </p>
              <p className={`text-sm font-bold ${isToday ? 'text-amber-700' : 'text-gray-900'}`}>
                {format(date, 'd')}
              </p>
              {isDisabled ? (
                <p className="text-[10px] text-gray-400 mt-0.5">No delivery</p>
              ) : count > 0 ? (
                <>
                  <Badge className="bg-amber-100 text-amber-700 text-[10px] mt-1 px-1.5">{count} order{count > 1 ? 's' : ''}</Badge>
                  {showRevenueSummary && dayRev > 0 && (
                    <p className="text-[10px] text-gray-500 mt-0.5">${dayRev.toFixed(0)}</p>
                  )}
                </>
              ) : (
                <p className="text-[10px] text-gray-400 mt-1">-</p>
              )}
              {isToday && (
                <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full" />
              )}
            </div>
          );
        })}
      </div>

      {/* Revenue Summary (admin only) */}
      {showRevenueSummary && (
        <div className="flex items-center gap-4 text-sm px-1" data-testid="week-summary">
          <span className="text-gray-500">Week total:</span>
          <span className="font-semibold text-gray-900">{weekSummary.orderCount} orders</span>
          <span className="text-gray-300">|</span>
          <span className="font-semibold text-gray-900">{weekSummary.totalQty} items</span>
          <span className="text-gray-300">|</span>
          <span className="font-semibold text-amber-600">${weekSummary.revenue.toFixed(2)}</span>
        </div>
      )}

      {/* Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={viewAll ? 'default' : 'outline'}
          size="sm"
          className={`h-7 text-xs ${viewAll ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
          onClick={handleToggleViewAll}
          data-testid="toggle-view-all"
        >
          {viewAll ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
          {viewAll ? 'Viewing All Orders' : 'Viewing Week Orders'}
        </Button>
        {!viewAll && selectedDay !== null && (
          <Badge variant="outline" className="text-xs">
            Filtered: {DAY_LABELS[selectedDay]} {format(weekDates[selectedDay], 'MMM d')}
          </Badge>
        )}
      </div>
    </div>
  );
};
